import api from './api';

const POPUP_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

function getBackendOrigin() {
  try {
    return new URL(process.env.REACT_APP_API_LOCAL_URL).origin;
  } catch {
    return window.location.origin;
  }
}

function getBackendBase() {
  return (process.env.REACT_APP_API_LOCAL_URL || '').replace(/\/$/, '');
}

class NiboOAuthService {
  /**
   * Executes the full backend-centralised Nibo OAuth flow:
   *
   * 1. Opens a popup pointing to about:blank (must be within a user gesture).
   * 2. Calls POST /oauth/nibo/launch (authenticated) to get a launch token.
   * 3. Navigates the popup to GET /oauth/nibo/start?t={token} on the backend.
   *    The backend redirects the popup to Nibo's authorization page.
   * 4. Nibo redirects back to the backend's fixed redirect_uri (/nibo/oauth/exchange),
   *    which exchanges the code, stores the org list in-memory, and posts a message.
   * 5. Calls GET /oauth/nibo/organizations?syncId={syncId} (authenticated) to
   *    retrieve the organization list stored by the callback.
   *
   * Returns { owner, organizations: [{ label, value, alreadyExists }] }.
   * No authorization code is ever forwarded to the frontend.
   */
  static async initiateAuth(syncId) {
    // Open popup synchronously BEFORE any async call so it stays within the
    // user-gesture context. Browsers block window.open() called after an await.
    const top = Math.floor((window.screen.height / 2) - 300);
    const left = Math.floor((window.screen.width / 2) - 300);
    const popupName = `NiboOAuth_${syncId}_${Date.now()}`;

    const popup = window.open(
      'about:blank',
      popupName,
      `width=600,height=700,popup=1,top=${top},left=${left}`,
    );

    if (!popup) {
      throw new Error(
        'Não foi possível abrir o popup de autorização. ' +
        'Verifique se popups estão bloqueados no seu navegador.',
      );
    }

    // Step 1: obtain short-lived launch token (requires valid JWT)
    let launchToken;
    try {
      const launchResponse = await api.post('/oauth/nibo/launch', { syncId });
      if (launchResponse.status !== 200) {
        popup.close();
        throw new Error(`Erro ao obter token de launch: status ${launchResponse.status}`);
      }
      launchToken = launchResponse.data?.launchToken;
      if (!launchToken) {
        popup.close();
        throw new Error('Launch token não recebido do servidor.');
      }
    } catch (err) {
      try { popup.close(); } catch (_) { /* ignore */ }
      throw err;
    }

    const backendBase = getBackendBase();
    const backendOrigin = getBackendOrigin();
    const popupUrl = `${backendBase}/oauth/nibo/start?t=${encodeURIComponent(launchToken)}`;

    // Step 2: navigate the already-open popup to the backend OAuth start URL
    popup.location.href = popupUrl;

    // Step 3: wait for postMessage from backend callback page, then fetch orgs
    return new Promise((resolve, reject) => {
      let settled = false;

      function cleanup() {
        clearInterval(closedInterval);
        clearTimeout(timeoutHandle);
        window.removeEventListener('message', onMessage);
        settled = true;
      }

      async function onMessage(event) {
        if (event.origin !== backendOrigin) return;

        const { type, success, error, syncId: callbackSyncId } = event.data || {};
        if (type !== 'nibo-oauth') return;

        cleanup();

        if (!success) {
          reject(new Error(error || 'Autenticação com Nibo falhou.'));
          return;
        }

        // Step 4: fetch org list from the backend (authenticated)
        try {
          const orgsResponse = await api.get('/oauth/nibo/organizations', {
            params: { syncId: callbackSyncId || syncId }
          });
          if (orgsResponse.status !== 200) {
            reject(new Error(`Erro ao obter empresas: status ${orgsResponse.status}`));
            return;
          }
          resolve({
            owner: orgsResponse.data.owner,
            organizations: orgsResponse.data.organizations.map(org => ({
              label: org.name,
              value: org.id,
              alreadyExists: org.alreadyExists || false
            }))
          });
        } catch (fetchErr) {
          reject(new Error('Erro ao obter lista de empresas do Nibo.'));
        }
      }

      // Detect popup closure before the postMessage arrives
      const closedInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(closedInterval);
          clearTimeout(timeoutHandle);
          setTimeout(() => {
            if (!settled) {
              window.removeEventListener('message', onMessage);
              settled = true;
              reject(new Error('Popup foi fechado pelo usuário antes de concluir a autorização.'));
            }
          }, 500);
        }
      }, 500);

      const timeoutHandle = setTimeout(() => {
        cleanup();
        try { popup.close(); } catch (_) { /* ignore */ }
        reject(new Error('Timeout: a autorização OAuth2 não foi concluída em 5 minutos.'));
      }, POPUP_TIMEOUT_MS);

      window.addEventListener('message', onMessage);
    });
  }

  // Criar integração com Nibo (chamado pelo SyncHandler via save do sync)
  static async createIntegration(tenantId, owner, empresas) {
    try {
      const response = await api.post('/nibo/createIntegration', {
        tenantId,
        credentials: {
          owner,
          empresas
        }
      });

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Erro ao criar integração com Nibo: status ${response.status}`);
      }
    } catch (error) {
      console.error('Erro ao criar integração:', error);
      throw new Error('Não foi possível criar a integração com o Nibo. Tente novamente.');
    }
  }

  // Excluir integração com Nibo
  static async deleteIntegration(tenantId, externalId, owner) {
    try {
      const response = await api.delete('/nibo/deleteIntegration', {
        data: {
          tenantId,
          externalId,
          owner
        }
      });

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Erro ao excluir integração com Nibo: status ${response.status}`);
      }
    } catch (error) {
      console.error('Erro ao excluir integração:', error);
      throw new Error('Não foi possível excluir a integração com o Nibo. Tente novamente.');
    }
  }
}

export default NiboOAuthService;
